import { useQuery } from "@tanstack/react-query";
import { Share2 } from "lucide-react";
import { sizedUploadUrl } from "@/lib/imageUrl";

type SocialLink = {
  label?: string | null;
  url?: string | null;
  iconUrl?: string | null;
};

type SeoConfig = {
  siteName?: string | null;
  socialLinks?: SocialLink[] | null;
};

function normalizeLinks(raw: SocialLink[] | null | undefined): Array<{ label: string; url: string; iconUrl?: string }> {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const links: Array<{ label: string; url: string; iconUrl?: string }> = [];
  for (const item of raw) {
    const label = String(item?.label || "").trim();
    const url = String(item?.url || "").trim();
    const iconUrl = String(item?.iconUrl || "").trim();
    if (!label || !/^https?:\/\//i.test(url)) continue;
    const key = url.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    links.push({ label, url, ...(iconUrl ? { iconUrl } : {}) });
  }
  return links;
}

export function SocialLinksCard() {
  const { data } = useQuery<SeoConfig>({
    queryKey: ["/api/seo"],
    queryFn: async () => {
      const res = await fetch("/api/seo");
      if (!res.ok) return {};
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const links = normalizeLinks(data?.socialLinks);
  if (!links.length) return null;

  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#101826] p-5 shadow-xl">
      <div className="pointer-events-none absolute -right-16 -top-16 h-32 w-32 rounded-full bg-primary/10 blur-3xl" />
      <div className="relative flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-2xl bg-primary text-black shadow-lg shadow-primary/15">
          <Share2 className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-primary">Connect</div>
          <h2 className="text-lg font-black text-white">Follow {String(data?.siteName || "BullBuzz").trim() || "BullBuzz"}</h2>
        </div>
      </div>

      <div className="relative mt-5 flex flex-wrap items-center gap-3">
        {links.map((link) => (
          <a
            key={link.url}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Follow ${link.label}`}
            title={link.label}
            className="group grid h-12 w-12 place-items-center rounded-2xl border border-white/10 bg-white/[0.04] text-sm font-black uppercase text-primary transition-colors hover:border-primary/40 hover:bg-primary/10 hover:text-white"
          >
            {link.iconUrl ? (
              <img src={sizedUploadUrl(link.iconUrl, 96)} alt="" width={48} height={48} className="h-full w-full object-contain p-2.5" loading="lazy" decoding="async" />
            ) : (
              link.label.slice(0, 1)
            )}
          </a>
        ))}
      </div>
    </div>
  );
}
